<script lang="ts" setup>
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import type { Brand } from '~/services/BrandService';

const api = useApi();

const { user } = useUser();
const brand = defineModel<Brand | { id: null | string } | null>("brand", { required: true });

const { data: brands_data } = await api.brands.list({
  query: {
    pagination: 'none',
    value_map: true
  }
})

const nameVariable = makeVariable('brand.name');
const avatarVariable = makeVariable('brand.avatar');
const brands = computed(() => {
  const brands = (brands_data.value?.data ?? []) as unknown as Brand[];

  if (!Array.isArray(brands)) {
    return [];
  }

  return brands.map(brand => ({
    ...brand,
    name: brand.id === user.value.account.brand?.id ? `${brand.name} (Default)` : brand.name,
  }));
});

function handleBrandClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  target.closest("button")?.click();
}

const value = computed({
  get: () => brand.value?.id,
  set: (id) => {
    brand.value = id ? brands.value.find(brand => brand.id === id) ?? null : null;
  }
});
</script>

<template>
  <UISelectMenu v-model="value" value-attribute="id" :none-value="null" :options="brands" :ui="{
    wrapper: '',
  }" :ui-menu="{
    wrapper: '',
    container: 'max-w-fit',
  }">
    <template #label="{ selected }">
      <UPopover :popper="{ placement: 'top' }" mode="hover">
        <div class="flex items-center max-w-72 h-6" @click.stop="handleBrandClick">
          <div class="text-sm mr-2 opacity-50">
            Brand:
          </div>
          <div class="w-8">
            <UIAvatar :src="selected?.avatar ??
              user?.account?.brand?.avatar
              " class="rounded-full" size="xs" />
          </div>
          <span class="truncate">{{
            selected?.name ?? selected?.label ?? (user?.account?.brand?.name ? `${user?.account?.brand?.name} (Default)`
              : 'Default')
          }}</span>
        </div>
        <template #panel>
          <div class="rounded-lg p-2">
            <div class="flex items-center w-72 pb-2">
              <div class="w-8">
                <UICopy v-slot="{ copy, copied }" :source="avatarVariable">
                  <span v-if="copied" class="flex h-6 w-6 items-center justify-center bg-hd-neutral-100 rounded-full">
                    <FontAwesomeIcon :icon="faCheck" />
                  </span>
                  <UIAvatar v-else :src="selected?.avatar ??
                    user?.account?.brand
                      ?.avatar
                    " class="rounded-full" size="xs" @click.stop="copy()" />
                </UICopy>
              </div>
              <UICopy v-slot="{ copy, copied }" :source="nameVariable">
                <span class="truncate hover:underline" @click.stop="copy()">{{
                  copied
                    ? "copied!"
                    : (selected?.name ??
                      selected?.label ?? (user?.account?.brand?.name ? `${user?.account?.brand?.name} (Default)`
                        : 'Default'))
                }}</span>
              </UICopy>
            </div>
            <ViewBrandStyle :brand="selected && selected.id
              ? selected
              : user?.account?.brand
              " :fallback="user?.account?.brand" class="h-24 w-72" copy-mode="variable" copyable />
          </div>
        </template>
      </UPopover>
    </template>
    <template #start>
      <div class="pb-2 pt-1 flex items-center gap-2 px-1">
        <UButton label="Reset to default" color="white" class="flex-1 justify-center" size="sm"
          @click="brand = { id: null }" />
        <HdPopoverTip
          :text="`Sets the selected brand to be the default brand which is '${user.account.brand?.name ?? 'N/A'}'.`"
          text-class="text-sm p-2" />
      </div>
    </template>
    <template #option="{ option, index }">
      <div class="flex flex-col gap-3 p-1.5">
        <div class="flex items-center w-72">
          <div class="w-8">
            <UIAvatar :src="option?.avatar ??
              user?.account?.brand?.avatar
              " class="rounded-full" size="xs" />
          </div>
          <span class="truncate">{{
            option.name ?? option.label
          }}</span>
        </div>
        <ViewBrandStyle v-if="
          option?.id
            ? option
            : user?.account?.brand
        " :brand="option.id
          ? option
          : user?.account?.brand
          " class="h-24 w-72" />
      </div>
    </template>
  </UISelectMenu>
</template>